"use client";

/**
 * useNavOverflow — content-aware ("priority+") fit measurement for the desktop navbar.
 *
 * A fixed CSS breakpoint can't know whether a given page's links actually fit: that depends on the
 * link count and label lengths, not the viewport width. This hook measures the real widths instead
 * and reports how many links fit inline, whether the rest must spill into a "More" dropdown, and
 * which logo variant (wide wordmark vs compact icon) the available space affords.
 *
 * It is container-based (reads the nav row's own width), so it works identically on the public
 * viewport and inside the resizable editor canvas, with no `window`-width assumptions.
 *
 * Measurement reads a hidden mirror row that the caller renders (always containing ALL items plus
 * dedicated nodes for both logo variants and the "More" button), tagged with `data-nm` attributes:
 *   - `[data-nm="items"]`  — wrapper whose children are the full set of nav items (one per item)
 *   - `[data-nm="wordmark"]` / `[data-nm="icon"]` — the two logo variants at their rendered cap
 *   - `[data-nm="more"]`   — the overflow trigger button
 * The CTA is measured from the live bar via `ctaRef` (its width is independent of the chosen tier).
 */

import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { NAVBAR_DESKTOP_GAP_PX, NAVBAR_GROUP_MIN_GAP_PX } from "../_logic/constants";

/** Which logo to show: the wide wordmark when there's room, the compact icon when space is tight. */
export type NavLogoVariant = "wordmark" | "icon";

interface NavOverflowState {
  /** Number of leading nav items that fit inline; the rest belong in the overflow menu. */
  visibleCount: number;
  /** Whether any items overflow (i.e. the "More" dropdown should render). */
  hasOverflow: boolean;
  /** Logo variant the available width affords. */
  logoVariant: NavLogoVariant;
  /**
   * `max(logoWidth, ctaWidth)` in px — the width to reserve for BOTH outer grid columns so they stay
   * equal. Equal `1fr` tracks alone don't guarantee this: CSS Grid freezes whichever `1fr` track's
   * content is wider at its own size and hands the rest of the leftover space to the other track, so
   * a lopsided logo/CTA pairing (especially under a tightly-packed row, i.e. exactly when overflow
   * trimming is active) pushes the centered nav-links column off the row's true center.
   */
  sideColumnPx: number;
  /** False until the first measurement resolves — used to keep the bar hidden so it never flashes. */
  measured: boolean;
}

interface UseNavOverflowResult extends NavOverflowState {
  /** Attach to the full-width nav row whose available width drives the fit. */
  containerRef: React.RefObject<HTMLDivElement>;
  /** Attach to the hidden mirror row (see file header for the required `data-nm` structure). */
  measureRef: React.RefObject<HTMLDivElement>;
  /** Attach to the live CTA wrapper so its width is reserved. */
  ctaRef: React.RefObject<HTMLDivElement>;
}

/**
 * Measure the desktop navbar and decide how many links fit, whether to overflow, and which logo.
 *
 * @param revalidateKey - A string that changes whenever the measured content changes (item labels,
 *   CTA text, logo URLs). A change forces a re-measure. Resize is handled automatically.
 * @returns Fit state plus the three refs the caller must attach (`containerRef`, `measureRef`, `ctaRef`).
 */
export function useNavOverflow(revalidateKey: string): UseNavOverflowResult {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  const [state, setState] = useState<NavOverflowState>({
    visibleCount: 0,
    hasOverflow: false,
    logoVariant: "wordmark",
    sideColumnPx: 0,
    measured: false,
  });

  const measure = useCallback(() => {
    const container = containerRef.current;
    const measurer = measureRef.current;
    if (!container || !measurer) return;

    // Commit only when the fit actually changed. ResizeObserver fires at frame rate during a drag
    // resize; without this guard every tick allocates a fresh object and re-renders the navbar.
    const commit = (next: NavOverflowState): void => {
      setState((prev) =>
        prev.visibleCount === next.visibleCount &&
        prev.hasOverflow === next.hasOverflow &&
        prev.logoVariant === next.logoVariant &&
        prev.sideColumnPx === next.sideColumnPx &&
        prev.measured === next.measured
          ? prev
          : next
      );
    };

    const itemsNode = measurer.querySelector<HTMLElement>('[data-nm="items"]');
    if (!itemsNode) return;

    const itemWidths = Array.from(itemsNode.children).map((c) => (c as HTMLElement).offsetWidth);
    const n = itemWidths.length;

    const wordmarkW = measurer.querySelector<HTMLElement>('[data-nm="wordmark"]')?.offsetWidth ?? 0;
    const iconW = measurer.querySelector<HTMLElement>('[data-nm="icon"]')?.offsetWidth ?? 0;
    const moreW = measurer.querySelector<HTMLElement>('[data-nm="more"]')?.offsetWidth ?? 0;
    const ctaW = ctaRef.current?.offsetWidth ?? 0;

    const cs = window.getComputedStyle(container);
    const padX = parseFloat(cs.paddingLeft || "0") + parseFloat(cs.paddingRight || "0");
    const available = container.clientWidth - padX;

    const gap = NAVBAR_DESKTOP_GAP_PX;
    const groupGaps = NAVBAR_GROUP_MIN_GAP_PX * 2; // logo↔links and links↔CTA

    // Prefix sums so the width of the first `count` items is O(1).
    const prefix: number[] = [0];
    for (let i = 0; i < n; i++) prefix.push(prefix[i] + itemWidths[i]);
    const itemsWidth = (count: number): number => (count <= 0 ? 0 : prefix[count] + gap * (count - 1));

    // The width reserved for BOTH outer grid columns so they stay equal (see `sideColumnPx` on
    // `NavOverflowState`) — single source of truth so this can't drift from what `fits()` checks.
    const sideColumnFor = (logoW: number): number => Math.max(logoW, ctaW);

    // Does logo + `count` inline links (+ optional "More") + CTA fit within the row? Models the
    // actual rendered grid, which reserves `sideColumnFor(logoW)` on BOTH outer columns — not
    // `logoW + ctaW`, which understates the space used whenever the two widths differ.
    const fits = (logoW: number, count: number, reserveMore: boolean): boolean => {
      const moreW2 = reserveMore ? moreW + (count > 0 ? gap : 0) : 0;
      const content = 2 * sideColumnFor(logoW) + itemsWidth(count) + moreW2 + groupGaps;
      return content <= available;
    };

    // Largest leading run of items that fits.
    const maxFit = (logoW: number, reserveMore: boolean): number => {
      let count = 0;
      for (let i = 1; i <= n; i++) {
        if (fits(logoW, i, reserveMore)) count = i;
        else break;
      }
      return count;
    };

    // Tier 1: wordmark with every link inline.
    if (n === 0 || fits(wordmarkW, n, false)) {
      commit({
        visibleCount: n,
        hasOverflow: false,
        logoVariant: "wordmark",
        sideColumnPx: sideColumnFor(wordmarkW),
        measured: true,
      });
      return;
    }
    // Tier 1b/2 both use the compact icon logo, so they share the same side-column reservation.
    const iconSideColumnPx = sideColumnFor(iconW);

    // Tier 1b: still all links, but only with the compact icon logo (no "More" yet).
    if (fits(iconW, n, false)) {
      commit({
        visibleCount: n,
        hasOverflow: false,
        logoVariant: "icon",
        sideColumnPx: iconSideColumnPx,
        measured: true,
      });
      return;
    }
    // Tier 2: icon logo + as many links as fit, remainder into "More".
    const count = maxFit(iconW, true);
    commit({
      visibleCount: count,
      hasOverflow: true,
      logoVariant: "icon",
      sideColumnPx: iconSideColumnPx,
      measured: true,
    });
  }, []);

  useLayoutEffect(() => {
    measure();
    const container = containerRef.current;
    const measurer = measureRef.current;
    const cta = ctaRef.current;
    if (!container) return;
    // Observe the row (viewport/canvas resizes), the mirror (late image/font load changes widths),
    // and the live CTA (its width is now baked into a fixed-px grid column via `sideColumnPx` — if
    // its content grows without the row itself resizing, e.g. text-only zoom, that column must be
    // re-measured or it renders too narrow for the CTA's actual content). Safe from feedback loops
    // only because the CTA wrapper is `justify-self-end`/`shrink-0` in the renderer, i.e. its own
    // width never stretches to fill the grid column it measures into — keep it that way.
    const ro = new ResizeObserver(() => measure());
    ro.observe(container);
    if (measurer) ro.observe(measurer);
    if (cta) ro.observe(cta);
    return () => ro.disconnect();
  }, [measure, revalidateKey]);

  return { containerRef, measureRef, ctaRef, ...state };
}
